import { useEffect, useRef } from "react";
import { Sparkles, Loader2, RefreshCcw, X } from "lucide-react";
import { AIMarkdown } from "@/components/revenueos/AIMarkdown";

/**
 * AIInsightPanel — B-13: insight renders in place, anchored to the requesting control.
 * Shows a visible loading state while generating and scrolls into view when active.
 */
export function AIInsightPanel({
  title = "AI Insight",
  insight,
  loading = false,
  error = null,
  onRegenerate,
  onDismiss,
  emptyText = "No AI insight yet.",
  anchorId,
  className = "",
  markdown = false,
  alwaysShow = false,
  accent = "copper",
  regenerateLabel,
}) {
  const ref = useRef(null);
  const blue = accent === "navy";

  useEffect(() => {
    if (loading) {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [loading]);

  const hasContent = Boolean(insight || loading || error);
  if (!alwaysShow && !hasContent) return null;

  return (
    <div
      ref={ref}
      id={anchorId}
      data-testid="ai-insights"
      className={`border p-3 md:p-4 ${blue ? "border-[#BBD8E7] bg-[#EEF4F7] rounded-[20px] shadow-[0_6px_18px_rgba(7,83,154,0.05)]" : "border-[var(--copper)]/25 bg-[var(--copper-glow)] rounded-md"} ${className}`}
    >
      <div className="flex items-center gap-2 mb-2 min-h-[1.25rem]">
        <Sparkles size={13} className={`${blue ? "text-[#07539A]" : "text-[var(--copper)]"} shrink-0`} />
        <span className={`text-[11px] uppercase tracking-widest ${blue ? "text-[#07539A] font-semibold" : "text-[var(--copper)]"}`}>{title}</span>
        {loading && (
          <span className="ml-2 text-[10px] text-[var(--text-muted)] flex items-center gap-1.5">
            <Loader2 size={11} className={`animate-spin ${blue ? "text-[#07539A]" : "text-[var(--copper)]"}`} />
            Generating…
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {onRegenerate && !loading && (
            <button
              type="button"
              onClick={onRegenerate}
              data-testid="ai-regenerate"
              title="Refresh AI insight"
              className={`${blue ? "text-[#07539A] hover:text-[#063F73] border-[#8EC9E3] hover:bg-[#DDF2FB]" : "text-[var(--copper)] hover:text-[var(--copper-strong)] border-[var(--copper)]/30"} ${regenerateLabel ? "h-8 px-3 rounded-xl border bg-white text-[11px] font-medium flex items-center gap-1.5" : "p-1"}`}
            >
              <RefreshCcw size={12} />
              {regenerateLabel && <span>{regenerateLabel}</span>}
            </button>
          )}
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              data-testid="ai-insight-dismiss"
              className="text-[var(--text-dim)] hover:text-[var(--text)] p-1"
              aria-label="Dismiss insight"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      <div className="text-[13px] text-[var(--text)] leading-relaxed min-h-[2.5rem] overflow-visible">
        {loading ? (
          <div className="space-y-2 py-1" data-testid="ai-insight-loading">
            <div className={`h-2.5 rounded animate-pulse w-full ${blue ? "bg-[#07539A]/15" : "bg-[var(--copper)]/15"}`} />
            <div className={`h-2.5 rounded animate-pulse w-[92%] ${blue ? "bg-[#07539A]/10" : "bg-[var(--copper)]/10"}`} />
            <div className={`h-2.5 rounded animate-pulse w-[78%] ${blue ? "bg-[#07539A]/10" : "bg-[var(--copper)]/10"}`} />
          </div>
        ) : error ? (
          <span className="text-[var(--danger)]">{error}</span>
        ) : insight ? (
          markdown ? <AIMarkdown>{insight}</AIMarkdown> : <div className="whitespace-pre-wrap">{insight}</div>
        ) : (
          <span className="text-[var(--text-dim)] italic">{emptyText}</span>
        )}
      </div>
    </div>
  );
}
